// Mock data — no backend required

const BLOG_POSTS = [
  {
    id: 1,
    slug: "building-scalable-microservices",
    title: "Building Scalable Microservices with Node.js",
    category: "Engineering",
    excerpt: "A deep dive into designing microservices that scale horizontally without sacrificing developer experience.",
    content: `Microservices have become the de-facto architecture for large-scale systems. In this post we walk through the key decisions we made while building a platform that handles 50k requests per second.\n\nWe'll cover service discovery, inter-service communication patterns, and how we use Kubernetes to orchestrate everything in production.\n\nThe biggest lesson: start with a monolith, extract services only when you feel the pain. Premature decomposition is the root of all evil in distributed systems.`,
    author: "Lena Park",
    author_role: "Chief Technology Officer",
    read_time: "8 min read",
    image: "https://images.pexels.com/photos/29506609/pexels-photo-29506609.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    published_at: "2024-03-15",
    tags: ["Node.js", "Microservices", "Kubernetes"],
  },
  {
    id: 2,
    slug: "llm-rag-pipelines-production",
    title: "LLM RAG Pipelines in Production: What We Learned",
    category: "AI/ML",
    excerpt: "Six months of running retrieval-augmented generation in production — the wins, the failures, and the surprises.",
    content: `RAG (Retrieval-Augmented Generation) sounds simple on paper: embed your docs, retrieve relevant chunks, pass them to an LLM. In practice, the devil is in the details.\n\nWe've been running RAG pipelines for three enterprise clients over the past six months. Here's what we learned about chunking strategies, embedding models, re-ranking, and latency budgets.\n\nThe most impactful change we made was switching from naive top-k retrieval to a hybrid BM25 + dense retrieval approach. Accuracy jumped 23% overnight.`,
    author: "Sarah Chen",
    author_role: "AI Practice Lead",
    read_time: "12 min read",
    image: "https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    published_at: "2024-04-02",
    tags: ["AI", "LLM", "RAG", "Python"],
  },
  {
    id: 3,
    slug: "design-systems-at-scale",
    title: "Design Systems at Scale: Lessons from 50 Products",
    category: "Design",
    excerpt: "How we built and maintained a design system that serves 50+ products across 3 platforms without going insane.",
    content: `A design system is a living product. It needs the same care, versioning, and governance as any other piece of software your team ships.\n\nAfter building and maintaining design systems for over 50 products, we've developed strong opinions about what works and what doesn't.\n\nThe single biggest mistake teams make: treating the design system as a side project. It needs a dedicated owner, a clear contribution model, and a release cadence.`,
    author: "Priya Patel",
    author_role: "Design Director",
    read_time: "7 min read",
    image: "https://images.pexels.com/photos/196644/pexels-photo-196644.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    published_at: "2024-04-18",
    tags: ["Design", "Figma", "React"],
  },
  {
    id: 4,
    slug: "kubernetes-cost-optimization",
    title: "Cutting Kubernetes Costs by 40% Without Sacrificing Reliability",
    category: "DevOps",
    excerpt: "Practical techniques we used to dramatically reduce cloud spend for a high-traffic SaaS platform.",
    content: `Cloud bills have a way of sneaking up on you. One of our clients came to us with a $80k/month Kubernetes bill and a mandate to cut it in half.\n\nWe didn't get to 50%, but we got to 40% — and we did it without a single incident. Here's the playbook: right-sizing nodes, spot instance strategies, namespace-level resource quotas, and smarter autoscaling policies.\n\nThe biggest win came from switching from cluster autoscaler to Karpenter. Provisioning time dropped from 4 minutes to 45 seconds, and bin-packing improved dramatically.`,
    author: "David Kim",
    author_role: "Principal Engineer",
    read_time: "10 min read",
    image: "https://images.pexels.com/photos/1181354/pexels-photo-1181354.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    published_at: "2024-05-05",
    tags: ["DevOps", "Kubernetes", "AWS", "Cost"],
  },
  {
    id: 5,
    slug: "react-native-performance",
    title: "React Native Performance: From 20fps to 60fps",
    category: "Mobile",
    excerpt: "A systematic approach to diagnosing and fixing performance bottlenecks in a large React Native app.",
    content: `Performance in React Native is a multi-layered problem. The JS thread, the UI thread, the bridge (or JSI in new arch) — each can be a bottleneck.\n\nWe inherited a React Native app with chronic jank on mid-range Android devices. After three weeks of profiling and targeted fixes, we hit a consistent 60fps on devices as old as the Galaxy A32.\n\nKey fixes: moving heavy computations off the main thread with worklets, virtualizing long lists properly, and eliminating unnecessary re-renders with careful memoization.`,
    author: "Aisha Bello",
    author_role: "Mobile Engineering Lead",
    read_time: "9 min read",
    image: "https://images.pexels.com/photos/607812/pexels-photo-607812.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    published_at: "2024-05-20",
    tags: ["React Native", "Mobile", "Performance"],
  },
  {
    id: 6,
    slug: "terraform-multi-account-aws",
    title: "Terraform Multi-Account AWS: Our Reference Architecture",
    category: "Cloud",
    excerpt: "How we structure Terraform for large AWS organisations with dozens of accounts and teams.",
    content: `Managing AWS at scale means managing dozens of accounts, hundreds of IAM roles, and thousands of resources. Terraform helps — but only if you structure it well.\n\nWe've settled on a reference architecture that uses Terragrunt for DRY configurations, a dedicated accounts module, and a strict separation between platform and application infrastructure.\n\nThe key insight: treat your AWS organisation like a product. It has users (your engineers), features (guardrails, networking, IAM), and a release process.`,
    author: "David Kim",
    author_role: "Principal Engineer",
    read_time: "11 min read",
    image: "https://images.pexels.com/photos/1181675/pexels-photo-1181675.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    published_at: "2024-06-01",
    tags: ["AWS", "Terraform", "Cloud", "DevOps"],
  },
];

const PROJECTS = [
  {
    id: 1,
    slug: "finflow-banking-platform",
    title: "FinFlow Banking Platform",
    summary: "A next-generation digital banking platform serving 2M+ customers across Southeast Asia.",
    client: "FinFlow Inc.",
    industry: ["FinTech"],
    services: ["Web Application", "Mobile App", "Cloud"],
    technologies: ["React", "React Native", "Node.js", "AWS", "PostgreSQL", "Kubernetes"],
    timeline: "14 months",
    team_size: "12 engineers",
    image: "https://images.pexels.com/photos/6801648/pexels-photo-6801648.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    challenge: "FinFlow needed to replace a 15-year-old core banking system while keeping 2 million active customers online. Zero downtime migration was non-negotiable.",
    solution: "We designed a strangler-fig migration pattern, routing traffic incrementally to the new platform while the legacy system ran in parallel. Feature flags controlled the rollout at the customer level.",
    metrics: [
      { label: "Customers migrated", value: "2M+", detail: "Zero downtime during migration" },
      { label: "Transaction latency", value: "−67%", detail: "P99 dropped from 900ms to 300ms" },
      { label: "Infrastructure cost", value: "−42%", detail: "Compared to legacy on-prem" },
    ],
    testimonial: {
      quote: "Nexus delivered what three previous vendors couldn't. The migration was flawless and the new platform is a joy to build on.",
      name: "James Okafor",
      role: "CTO, FinFlow Inc.",
    },
    gallery: [
      "https://images.pexels.com/photos/6801648/pexels-photo-6801648.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      "https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    ],
  },
  {
    id: 2,
    slug: "medtrack-health-platform",
    title: "MedTrack Health Platform",
    summary: "An AI-powered patient monitoring platform used by 300+ hospitals across North America.",
    client: "MedTrack Health",
    industry: ["Healthcare"],
    services: ["Web Application", "AI/ML", "Cloud"],
    technologies: ["React", "Python", "FastAPI", "AWS", "TensorFlow", "PostgreSQL"],
    timeline: "10 months",
    team_size: "8 engineers",
    image: "https://images.pexels.com/photos/4386466/pexels-photo-4386466.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    challenge: "Hospitals were drowning in patient data but couldn't act on it fast enough. Nurses spent 40% of their time on documentation instead of care.",
    solution: "We built an ML pipeline that triages patient alerts by severity, auto-populates clinical notes from voice input, and surfaces early warning signals 4 hours before deterioration.",
    metrics: [
      { label: "Documentation time", value: "−40%", detail: "Nurses reclaim 3+ hours per shift" },
      { label: "Early warnings", value: "4h", detail: "Average lead time before deterioration" },
      { label: "Hospitals onboarded", value: "300+", detail: "Across 12 US states" },
    ],
    testimonial: {
      quote: "The AI triage alone has saved lives. We can say that without hyperbole.",
      name: "Dr. Amara Singh",
      role: "Chief Medical Officer, MedTrack",
    },
    gallery: [
      "https://images.pexels.com/photos/4386466/pexels-photo-4386466.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      "https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    ],
  },
  {
    id: 3,
    slug: "shopstream-ecommerce",
    title: "ShopStream E-Commerce Engine",
    summary: "A headless commerce platform powering $500M+ in annual GMV for a global retail brand.",
    client: "ShopStream Retail",
    industry: ["E-commerce", "Retail"],
    services: ["Web Application", "Mobile App", "DevOps"],
    technologies: ["Next.js", "React Native", "Go", "Kubernetes", "Redis", "PostgreSQL"],
    timeline: "8 months",
    team_size: "10 engineers",
    image: "https://images.pexels.com/photos/5632399/pexels-photo-5632399.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    challenge: "The client's existing Magento store was collapsing under Black Friday traffic. They needed a platform that could handle 10x normal load without degradation.",
    solution: "We rebuilt the storefront as a headless Next.js app backed by a Go microservices layer. Edge caching, read replicas, and a queue-based checkout flow eliminated the bottlenecks.",
    metrics: [
      { label: "Peak throughput", value: "50k/s", detail: "Requests per second on Black Friday" },
      { label: "Conversion rate", value: "+18%", detail: "Faster pages = more purchases" },
      { label: "Annual GMV", value: "$500M+", detail: "Processed through the platform" },
    ],
    testimonial: {
      quote: "Black Friday was the smoothest in our company's history. Not a single incident.",
      name: "Rachel Torres",
      role: "VP Engineering, ShopStream",
    },
    gallery: [
      "https://images.pexels.com/photos/5632399/pexels-photo-5632399.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      "https://images.pexels.com/photos/230544/pexels-photo-230544.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    ],
  },
  {
    id: 4,
    slug: "learnloop-edtech",
    title: "LearnLoop EdTech Platform",
    summary: "An adaptive learning platform with 1M+ learners across 40 countries.",
    client: "LearnLoop",
    industry: ["Education", "SaaS"],
    services: ["Web Application", "AI/ML", "UI/UX Design"],
    technologies: ["React", "Python", "Django", "AWS", "PostgreSQL", "Redis"],
    timeline: "12 months",
    team_size: "9 engineers",
    image: "https://images.pexels.com/photos/4145153/pexels-photo-4145153.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    challenge: "One-size-fits-all online courses had completion rates below 10%. LearnLoop wanted to build a platform that adapts to each learner's pace, style, and goals.",
    solution: "We built an adaptive engine that adjusts content difficulty and format in real time based on engagement signals. Spaced repetition and mastery-based progression replaced linear course structures.",
    metrics: [
      { label: "Course completion", value: "+3.4×", detail: "Compared to industry average" },
      { label: "Active learners", value: "1M+", detail: "Across 40 countries" },
      { label: "NPS score", value: "72", detail: "World-class for EdTech" },
    ],
    testimonial: {
      quote: "Our completion rates went from embarrassing to industry-leading in under a year.",
      name: "Yuki Tanaka",
      role: "CEO, LearnLoop",
    },
    gallery: [
      "https://images.pexels.com/photos/4145153/pexels-photo-4145153.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      "https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    ],
  },
  {
    id: 5,
    slug: "logix-supply-chain",
    title: "Logix Supply Chain Intelligence",
    summary: "Real-time supply chain visibility and AI-driven disruption prediction for a Fortune 500 manufacturer.",
    client: "Logix Manufacturing",
    industry: ["Manufacturing", "Logistics"],
    services: ["Web Application", "AI/ML", "Cloud"],
    technologies: ["Vue.js", "Python", "FastAPI", "Azure", "Kafka", "PostgreSQL"],
    timeline: "11 months",
    team_size: "7 engineers",
    image: "https://images.pexels.com/photos/1427541/pexels-photo-1427541.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    challenge: "COVID exposed how blind large manufacturers are to their supply chains. Logix had 4,000 suppliers and no real-time visibility beyond tier 1.",
    solution: "We built a graph-based supply chain model that ingests data from 4,000 suppliers in real time. An ML model predicts disruptions 2 weeks out with 78% accuracy.",
    metrics: [
      { label: "Suppliers monitored", value: "4,000+", detail: "Real-time visibility" },
      { label: "Disruption prediction", value: "78%", detail: "Accuracy 2 weeks ahead" },
      { label: "Inventory cost", value: "−$12M", detail: "Annual savings from better planning" },
    ],
    testimonial: {
      quote: "We went from flying blind to having a crystal ball. The ROI was evident within 90 days.",
      name: "Marcus Webb",
      role: "SVP Operations, Logix",
    },
    gallery: [
      "https://images.pexels.com/photos/1427541/pexels-photo-1427541.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      "https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    ],
  },
  {
    id: 6,
    slug: "pulse-saas-analytics",
    title: "Pulse SaaS Analytics",
    summary: "A product analytics platform processing 10B+ events per month for 500+ SaaS companies.",
    client: "Pulse Analytics",
    industry: ["SaaS"],
    services: ["Web Application", "Cloud", "DevOps"],
    technologies: ["React", "Go", "Kubernetes", "GCP", "Kafka", "ClickHouse"],
    timeline: "9 months",
    team_size: "11 engineers",
    image: "https://images.pexels.com/photos/590022/pexels-photo-590022.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    challenge: "Pulse was ingesting billions of events but queries took minutes. Customers were churning to competitors with faster dashboards.",
    solution: "We migrated the analytics backend from PostgreSQL to ClickHouse and redesigned the ingestion pipeline with Kafka. Query times dropped from minutes to sub-second.",
    metrics: [
      { label: "Events processed", value: "10B+", detail: "Per month" },
      { label: "Query latency", value: "−99%", detail: "From minutes to milliseconds" },
      { label: "Customer churn", value: "−60%", detail: "After the performance relaunch" },
    ],
    testimonial: {
      quote: "The performance improvement was so dramatic our customers thought we'd launched a new product.",
      name: "Nina Kowalski",
      role: "CTO, Pulse Analytics",
    },
    gallery: [
      "https://images.pexels.com/photos/590022/pexels-photo-590022.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      "https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    ],
  },
];

const SUBMISSIONS = [
  {
    id: 1,
    name: "Alex Rivera",
    email: "alex@techstartup.io",
    company: "TechStartup IO",
    service: "Custom Software",
    budget: "$50k – $100k",
    message: "We're building a B2B SaaS platform for construction project management. Looking for a team to help us go from MVP to production-ready. Timeline is 6 months.",
    created_at: "2024-06-10T09:23:00Z",
  },
  {
    id: 2,
    name: "Maria Santos",
    email: "maria@globalretail.com",
    company: "Global Retail Co.",
    service: "Web & Mobile",
    budget: "$100k+",
    message: "We need to rebuild our customer-facing mobile app. Current app has terrible reviews due to performance issues. We have 500k active users and need a smooth migration.",
    created_at: "2024-06-12T14:05:00Z",
  },
  {
    id: 3,
    name: "Tom Nguyen",
    email: "tom.nguyen@aiventures.vc",
    company: "AI Ventures",
    service: "AI / ML",
    budget: "$10k – $50k",
    message: "We're a VC firm looking to add AI-powered deal flow analysis to our internal tools. Interested in an LLM-based solution that can process pitch decks and surface key insights.",
    created_at: "2024-06-14T11:30:00Z",
  },
];

// Simulate async delay like a real API
const delay = (ms = 200) => new Promise((res) => setTimeout(res, ms));

export async function getBlogPosts() {
  await delay();
  return [...BLOG_POSTS];
}

export async function getBlogPost(slug) {
  await delay();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  if (!post) throw new Error("Not found");
  return post;
}

export async function getProjects() {
  await delay();
  return [...PROJECTS];
}

export async function getProject(slug) {
  await delay();
  const project = PROJECTS.find((p) => p.slug === slug);
  if (!project) throw new Error("Not found");
  return project;
}

export async function getSubmissions() {
  await delay();
  return [...SUBMISSIONS];
}

export async function submitContact(form) {
  await delay(400);
  // In static mode, just resolve successfully
  return { ok: true };
}

// Admin mock store (in-memory, resets on page refresh)
let mockPosts = [...BLOG_POSTS];

export async function adminGetPosts() {
  await delay();
  return [...mockPosts];
}

export async function adminCreatePost(payload) {
  await delay(300);
  const post = { ...payload, id: Date.now(), tags: payload.tags || [] };
  mockPosts = [post, ...mockPosts];
  return post;
}

export async function adminUpdatePost(slug, patch) {
  await delay(300);
  mockPosts = mockPosts.map((p) => (p.slug === slug ? { ...p, ...patch } : p));
  return mockPosts.find((p) => p.slug === slug);
}

export async function adminDeletePost(slug) {
  await delay(300);
  mockPosts = mockPosts.filter((p) => p.slug !== slug);
  return { ok: true };
}

export function formatApiError(detail, fallback = "Something went wrong.") {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  return String(detail);
}
